import { createSvgEl } from '@learnforge/demo-render';

export interface TreeNode<T> {
  data: T;
  children: TreeNode<T>[];
  /** Render this node as a "…" placeholder standing in for omitted siblings. */
  ellipsis?: boolean;
}

export interface TreeRenderOptions<T> {
  root: TreeNode<T>;
  renderNode(node: TreeNode<T>, depth: number): { label: string; className: string };
  width: number;
  height: number;
  maxRenderedLeaves?: number;
  /** Depth whose nodes get the `level-active` class (highlight a whole layer). */
  highlightLevel?: number | null;
  onNodeClick?(node: TreeNode<T>, depth: number): void;
  /** Cap children shown per node; the omitted middle ones collapse to a "…". */
  maxChildren?: number;
}

export interface TreeHandle<T> {
  group: SVGGElement;
  update(opts: Partial<TreeRenderOptions<T>>): void;
  destroy(): void;
}

function treeDepth<T>(node: TreeNode<T>): number {
  if (node.children.length === 0) return 1;
  return 1 + Math.max(...node.children.map(treeDepth));
}

function countLeaves<T>(node: TreeNode<T>): number {
  if (node.children.length === 0) return 1;
  return node.children.reduce((s, c) => s + countLeaves(c), 0);
}

export function createTree<T>(parent: SVGGElement, initial: TreeRenderOptions<T>): TreeHandle<T> {
  const group = createSvgEl('g', { class: 'arith-tree' });
  parent.appendChild(group);
  let opts = { ...initial };

  function render(): void {
    while (group.firstChild) group.removeChild(group.firstChild);
    const depth = treeDepth(opts.root);
    const layerHeight = opts.height / Math.max(depth, 1);
    function place(node: TreeNode<T>, x0: number, x1: number, layer: number): void {
      const cx = (x0 + x1) / 2;
      const cy = layer * layerHeight + layerHeight / 2;
      const { label, className } = opts.renderNode(node, layer);
      if (node.children.length > 0) {
        // When a node has too many children, show the first/last few with a
        // single "…" standing in for the omitted middle ones.
        const max = opts.maxChildren;
        let items: (TreeNode<T> | 'ellipsis')[] = node.children;
        if (max !== undefined && node.children.length > max) {
          const head = Math.ceil(max / 2);
          const tail = max - head;
          items = [
            ...node.children.slice(0, head),
            'ellipsis',
            ...node.children.slice(node.children.length - tail),
          ];
        }
        const weights = items.map((it) => (it === 'ellipsis' ? 1 : countLeaves(it)));
        const total = weights.reduce((s, w) => s + w, 0) || 1;
        let cursor = x0;
        items.forEach((it, idx) => {
          const span = ((x1 - x0) * (weights[idx] ?? 1)) / total;
          const childX0 = cursor;
          const childX1 = cursor + span;
          const childCx = (childX0 + childX1) / 2;
          const childCy = (layer + 1) * layerHeight + layerHeight / 2;
          const edge = createSvgEl('line', {
            class: 'arith-tree-edge',
            x1: cx,
            y1: cy,
            x2: childCx,
            y2: childCy,
          });
          group.appendChild(edge);
          if (it === 'ellipsis' || it.ellipsis === true) {
            const dots = createSvgEl('text', {
              class: 'arith-tree-ellipsis',
              x: childCx,
              y: childCy,
              'text-anchor': 'middle',
              'dominant-baseline': 'central',
            });
            dots.textContent = '…';
            group.appendChild(dots);
          } else {
            place(it, childX0, childX1, layer + 1);
          }
          cursor = childX1;
        });
      }
      const activeCls = opts.highlightLevel === layer ? ' level-active' : '';
      const nodeG = createSvgEl('g', {
        class: `arith-tree-node ${className}${activeCls}`.trim(),
        transform: `translate(${cx}, ${cy})`,
      });
      if (opts.onNodeClick) {
        nodeG.style.cursor = 'pointer';
        nodeG.addEventListener('click', () => opts.onNodeClick?.(node, layer));
      }
      // Size the node to its label so multi-digit values stay inside the border.
      const w = Math.max(34, label.length * 9 + 14);
      const h = 26;
      const bg = createSvgEl('rect', {
        x: -w / 2,
        y: -h / 2,
        width: w,
        height: h,
        rx: 8,
      });
      nodeG.appendChild(bg);
      const text = createSvgEl('text', {
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
      });
      text.textContent = label;
      nodeG.appendChild(text);
      group.appendChild(nodeG);
    }
    place(opts.root, 0, opts.width, 0);
  }

  render();

  return {
    group,
    update(next: Partial<TreeRenderOptions<T>>) {
      opts = { ...opts, ...next };
      render();
    },
    destroy() {
      if (group.parentNode) group.parentNode.removeChild(group);
    },
  };
}
